import { useCallback, useEffect, useRef, useState } from "react";
import { Share2, Download, Trophy, Users } from "@/lib/icons";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

const W = 1080, H = 1080;
const TIER = [["full", "Full", "#34d399"], ["partial", "Partial", "#fbbf24"], ["showed_up", "Showed up", "#7dd3fc"]];

function rounded(ctx, x, y, w, h, r) {
  ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}

function fmtRange(r) {
  const f = (s) => new Date(s + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${f(r.start)} – ${f(r.end)}`;
}

export function drawWinsCard(canvas, w, withTitles) {
  const ctx = canvas.getContext("2d");
  canvas.width = W; canvas.height = H;
  ctx.fillStyle = "#1c1917"; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "rgba(251,191,36,0.08)"; ctx.beginPath(); ctx.arc(W - 80, 80, 320, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "#fbbf24"; ctx.font = "bold 28px ui-sans-serif, system-ui"; ctx.letterSpacing = "6px";
  ctx.fillText("MY WEEK IN HABITS", 80, 120);
  ctx.letterSpacing = "0px";
  ctx.fillStyle = "#fafaf9"; ctx.font = "800 72px Georgia, ui-serif, serif";
  ctx.fillText(w.display, 80, 210);
  ctx.fillStyle = "#a8a29e"; ctx.font = "500 30px ui-sans-serif, system-ui";
  ctx.fillText(`${fmtRange(w.range)} · ${w.level}`, 80, 262);

  const stats = [[w.checkins, "check-ins"], [w.active_days, "active days"], [w.best_streak, "day streak"], [w.points, "points"]];
  stats.forEach(([v, l], i) => {
    const x = 80 + i * 232, y = 320;
    ctx.fillStyle = "rgba(255,255,255,0.06)"; rounded(ctx, x, y, 208, 170, 28); ctx.fill();
    ctx.fillStyle = "#fbbf24"; ctx.font = "800 68px ui-monospace, monospace"; ctx.fillText(String(v), x + 26, y + 92);
    ctx.fillStyle = "#d6d3d1"; ctx.font = "600 24px ui-sans-serif, system-ui"; ctx.fillText(l, x + 26, y + 138);
  });

  ctx.fillStyle = "#a8a29e"; ctx.font = "bold 22px ui-sans-serif, system-ui"; ctx.letterSpacing = "4px"; ctx.fillText("EACH DAY", 80, 560); ctx.letterSpacing = "0px";
  const max = Math.max(1, ...w.days.map((d) => d.count));
  w.days.forEach((d, i) => {
    const x = 80 + i * 134, bh = Math.max(14, Math.round((d.count / max) * 150)), y = 750 - bh;
    ctx.fillStyle = d.count ? "#fbbf24" : "rgba(255,255,255,0.1)"; rounded(ctx, x, y, 100, bh, 14); ctx.fill();
    ctx.fillStyle = "#d6d3d1"; ctx.font = "600 24px ui-sans-serif, system-ui"; ctx.textAlign = "center";
    ctx.fillText(new Date(d.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "short" }), x + 50, 790);
    if (d.count) { ctx.fillStyle = "#1c1917"; ctx.font = "800 26px ui-monospace, monospace"; ctx.fillText(String(d.count), x + 50, y + Math.min(bh - 10, 36)); }
    ctx.textAlign = "left";
  });

  const total = Math.max(1, w.checkins);
  let x = 80;
  ctx.fillStyle = "#a8a29e"; ctx.font = "bold 22px ui-sans-serif, system-ui"; ctx.letterSpacing = "4px"; ctx.fillText("HOW IT WENT", 80, 860); ctx.letterSpacing = "0px";
  TIER.forEach(([k, , c]) => {
    const seg = Math.round((w.tiers[k] / total) * 920);
    if (seg > 0) { ctx.fillStyle = c; rounded(ctx, x, 885, Math.max(seg, 12), 34, 12); ctx.fill(); x += seg + 6; }
  });
  if (!w.checkins) { ctx.fillStyle = "rgba(255,255,255,0.1)"; rounded(ctx, 80, 885, 920, 34, 12); ctx.fill(); }
  let lx = 80;
  TIER.forEach(([k, label, c]) => {
    ctx.fillStyle = c; ctx.beginPath(); ctx.arc(lx + 10, 956, 10, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#d6d3d1"; ctx.font = "600 24px ui-sans-serif, system-ui"; ctx.fillText(`${label} ${w.tiers[k]}`, lx + 32, 965);
    lx += ctx.measureText(`${label} ${w.tiers[k]}`).width + 80;
  });

  if (withTitles && w.habits.length) {
    ctx.fillStyle = "#78716c"; ctx.font = "500 24px ui-sans-serif, system-ui";
    const line = w.habits.map((h) => `${h.keystone ? "★ " : ""}${h.title} ×${h.count}`).join("   ·   ");
    ctx.fillText(line.length > 90 ? line.slice(0, 88) + "…" : line, 80, 1020);
  } else {
    ctx.fillStyle = "#57534e"; ctx.font = "500 24px ui-sans-serif, system-ui"; ctx.fillText("Root & Rose · small things, adding up", 80, 1020);
  }
}

export function WeeklyWinsCard() {
  const [wins, setWins] = useState(null);
  const [withTitles, setWithTitles] = useState(false);
  const [open, setOpen] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => { api.get("/wins/weekly").then((r) => setWins(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (open && wins && canvasRef.current) drawWinsCard(canvasRef.current, wins, withTitles); }, [open, wins, withTitles]);

  const toBlob = useCallback(() => new Promise((res) => canvasRef.current.toBlob(res, "image/png")), []);
  const filename = () => `my-week-${wins.range.end}.png`;
  const download = async () => {
    const blob = await toBlob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename(); a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    toast.success("Saved — nothing about your wellbeing is on it, just your wins.");
  };
  const shareToCommunity = async () => {
    try {
      const r = await api.post("/wins/share");
      setWins((w) => ({ ...w, shared_today: { id: r.data.share_id, cheer_count: w.shared_today?.cheer_count || 0 } }));
      toast.success(r.data.updated ? "Your community card is refreshed." : "Shared with your community — counts and tiers only. Cheers land in your Buddy Cheers inbox.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't share right now");
    }
  };
  const share = async () => {
    const blob = await toBlob();
    const file = new File([blob], filename(), { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) {
      try { await navigator.share({ files: [file], title: "My week in habits" }); } catch (e) { /* user cancelled */ }
    } else { await download(); }
  };

  if (!wins) return null;
  return (
    <section className="warm-card p-5 sm:p-6" data-testid="weekly-wins-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-800"><Trophy className="w-3.5 h-3.5" /> Weekly wins</div>
          <p className="text-sm text-stone-600 mt-1">{wins.checkins} check-in{wins.checkins === 1 ? "" : "s"} across {wins.active_days} day{wins.active_days === 1 ? "" : "s"} this week. Turn it into a picture worth sharing — counts and tiers only, never your wellbeing.</p>
        </div>
        <button data-testid="wins-open-btn" onClick={() => setOpen((v) => !v)} className="text-xs font-semibold px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-amber-700">{open ? "Hide" : "Make my card"}</button>
      </div>
      {open && (
        <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,420px)_1fr] items-start">
          <canvas ref={canvasRef} data-testid="wins-canvas" className="w-full rounded-2xl shadow-lg" style={{ aspectRatio: "1 / 1" }} />
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm text-stone-700">
              <input type="checkbox" checked={withTitles} onChange={(e) => setWithTitles(e.target.checked)} data-testid="wins-titles-toggle" className="accent-amber-600" />
              Include my habit names <span className="text-xs text-stone-500">(off by default — only you decide what's visible)</span>
            </label>
            <div className="flex gap-2 flex-wrap">
              <button data-testid="wins-download-btn" onClick={download} className="amber-btn text-xs py-2 inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> Download PNG</button>
              <button data-testid="wins-share-btn" onClick={share} className="text-xs font-semibold px-4 py-2 rounded-full bg-stone-100 text-stone-800 hover:bg-stone-200 inline-flex items-center gap-1.5"><Share2 className="w-3.5 h-3.5" /> Share</button>
              {wins.can_share ? (
                <button data-testid="wins-community-share-btn" onClick={shareToCommunity} className="text-xs font-semibold px-4 py-2 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 inline-flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> {wins.shared_today ? "Refresh community card" : "Share to community"}
                </button>
              ) : (
                <Link to="/community" data-testid="wins-community-join-link" className="text-xs font-semibold px-4 py-2 rounded-full text-stone-500 hover:text-stone-800 inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Join the community to share for cheers</Link>
              )}
            </div>
            {wins.shared_today && <p className="text-xs text-emerald-800" data-testid="wins-shared-status">On the community Wins feed · {wins.shared_today.cheer_count || 0} cheer{wins.shared_today.cheer_count === 1 ? "" : "s"} so far. Habit names are never included there.</p>}
            <p className="text-xs text-stone-500">Shows {wins.display === "My week" ? "no name" : `your community handle “${wins.display}”`}, check-ins, streak, points, level and Full / Partial / Showed up counts. No moods, scales, notes or clinician details.</p>
          </div>
        </div>
      )}
    </section>
  );
}
